import { useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader } from "../components/PageHeader";
import { Chip, ErrorState, Skeleton } from "../components/States";
import { disconnectXero, startXeroConnect, xeroStatus } from "../lib/data";
import { fmtDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";

const RETURN_REASONS: Record<string, string> = {
  expired: "The Xero sign-in took too long or was already used. Try connecting again.",
  denied: "Access was not granted in Xero, so nothing was connected.",
  no_organisation: "No Xero organisation was chosen. Try again and pick your organisation.",
};

/** Read and clear the ?xero= result the API's OAuth callback adds when it sends a director back. */
function takeReturnResult(): { tone: "ok" | "error"; text: string } | null {
  const params = new URLSearchParams(window.location.search);
  const result = params.get("xero");
  if (!result) return null;
  window.history.replaceState(null, "", window.location.pathname + window.location.hash);
  if (result === "connected") return { tone: "ok", text: "Xero is connected. Accepted quotes can now be sent to Xero as draft invoices." };
  return { tone: "error", text: RETURN_REASONS[params.get("reason") ?? ""] ?? "The Xero sign-in did not complete. Try again." };
}

export function Integrations() {
  const { data, error, loading, reload } = useAsync(xeroStatus);
  const { notice, setNotice, fail, ok } = useNotice();
  const [busy, setBusy] = useState<"connect" | "disconnect" | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const r = takeReturnResult();
    if (r) setNotice(r);
  }, [setNotice]);

  async function connect() {
    setBusy("connect");
    setNotice(null);
    try {
      window.location.assign(await startXeroConnect());
    } catch (e) {
      fail(e);
      setBusy(null);
    }
  }

  async function disconnect() {
    setBusy("disconnect");
    setNotice(null);
    try {
      await disconnectXero();
      ok("Xero is disconnected. Invoices already in Xero are not affected.");
      setConfirming(false);
      reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Integrations"
        title="Xero"
        meta="Connect your Xero organisation so accepted quotes can be sent to Xero as draft invoices, with the customer set up as a Xero contact. You review and send the invoice in Xero as usual."
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {loading && !data && <Skeleton rows={2} columns={2} />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {data && !data.configured && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Not set up yet</h2></div>
          <p className="muted card__body">Xero has not been switched on for this site. Your platform provider needs to finish setting it up first.</p>
        </div>
      )}

      {data?.configured && (
        <div className="card">
          <div className="card__head">
            <h2 className="card__title">Xero connection</h2>
            <Chip value={data.connected ? "active" : data.status === "expired" ? "expired" : "draft"}>{data.connected ? "Connected" : data.status === "expired" ? "Needs reconnecting" : "Not connected"}</Chip>
          </div>
          <div className="stack card__body">
            {data.connected && (
              <p>
                Connected to <strong>{data.tenantName ?? "your Xero organisation"}</strong>
                {data.connectedAt && <span className="muted"> since {fmtDate(data.connectedAt)}</span>}.
              </p>
            )}
            {data.status === "expired" && <p>The connection to {data.tenantName ?? "Xero"} has expired. Reconnect to keep sending invoices.</p>}
            {!data.connected && data.status !== "expired" && <p className="muted">Only a director can connect Xero. You will be sent to Xero to sign in and choose the organisation, then brought back here.</p>}

            <div>
              {!data.connected && (
                <button type="button" className="btn btn--primary" disabled={busy !== null} aria-busy={busy === "connect"} onClick={connect}>
                  <Icon name="external" size={18} /> {busy === "connect" ? "Opening Xero" : data.status === "expired" ? "Reconnect Xero" : "Connect Xero"}
                </button>
              )}
              {data.connected && !confirming && (
                <button type="button" className="btn btn--outline btn--sm" disabled={busy !== null} onClick={() => setConfirming(true)}>
                  Disconnect
                </button>
              )}
              {data.connected && confirming && (
                <>
                  <p className="small">Disconnect Xero? Quotes can no longer be sent until a director connects it again.</p>
                  <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null} aria-busy={busy === "disconnect"} onClick={disconnect}>
                    {busy === "disconnect" ? "Disconnecting" : "Yes, disconnect"}
                  </button>{" "}
                  <button type="button" className="btn btn--outline btn--sm" disabled={busy !== null} onClick={() => setConfirming(false)}>
                    Keep connected
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
